import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';

// Simple, clean icons
const icons = {
  dashboard: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m8 5l4-4 4 4" />
    </svg>
  ),
  students: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  teachers: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  courses: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  departments: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  timetable: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  attendance: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  profile: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  menu: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  close: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  logout: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  ),
  sun: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  moon: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  ),
  announcements: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  ),
  results: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  )
};

// Clean navigation configuration
const navigationConfig = {
  coordinator: [
    { path: '/coordinator', label: 'Dashboard', icon: icons.dashboard },
    { path: '/students', label: 'Students', icon: icons.students },
    { path: '/teachers', label: 'Teachers', icon: icons.teachers },
    { path: '/courses', label: 'Courses', icon: icons.courses },
    { path: '/departments', label: 'Departments', icon: icons.departments },
    { path: '/timetable', label: 'Timetable', icon: icons.timetable },
    { path: '/attendance', label: 'Attendance', icon: icons.attendance },
    { path: '/results', label: 'Results', icon: icons.results }
  ],
  teacher: [
    { path: '/teacher', label: 'Dashboard', icon: icons.dashboard },
    { path: '/my-timetable', label: 'My Schedule', icon: icons.timetable },
    { path: '/attendance/take', label: 'Take Attendance', icon: icons.attendance }
  ],
  student: [
    { path: '/student', label: 'Dashboard', icon: icons.dashboard },
    { path: '/my-timetable', label: 'My Schedule', icon: icons.timetable },
    { path: '/attendance/me', label: 'My Attendance', icon: icons.attendance },
    { path: '/student/grades', label: 'Grades', icon: icons.courses },
    { path: '/student/announcements', label: 'Announcements', icon: icons.announcements },
    { path: '/student/profile', label: 'Profile', icon: icons.profile }
  ]
};

interface User {
  user_id: number;
  email: string;
  role: 'coordinator' | 'teacher' | 'student';
  name?: string;
}

const CleanNavbar: React.FC = () => {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const history = useHistory();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const user = useMemo((): User | null => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const navigation = useMemo(() => {
    if (!user) return [];
    return navigationConfig[user.role] || [];
  }, [user]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProfileDropdownOpen(false);
  }, [location.pathname]);

  // Toggle mobile menu with animation
  const toggleMobileMenu = useCallback(() => {
    console.log('Toggle mobile menu called, current state:', isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsMobileMenuOpen(false);
        setIsAnimating(false);
      }, 300);
    } else {
      setIsMobileMenuOpen(true);
    }
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const getRoleDisplayName = (role: string) => {
    const roles = {
      coordinator: 'Administrator',
      teacher: 'Teacher',
      student: 'Student'
    };
    return roles[role as keyof typeof roles] || role;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking on menu button or inside mobile menu
      if (target.closest('[data-mobile-menu-button]') || target.closest('[data-mobile-menu]')) {
        return;
      }
      setIsProfileDropdownOpen(false);
      if (isMobileMenuOpen) {
        setIsAnimating(true);
        setTimeout(() => {
          setIsMobileMenuOpen(false);
          setIsAnimating(false);
        }, 300);
      }
    };

    if (isProfileDropdownOpen || isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isProfileDropdownOpen, isMobileMenuOpen]);

  if (!user) {
    return null;
  }

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold text-lg">H</span>
              </div>
              <div className="flex flex-col">
                <h1 className="text-xl font-bold text-gray-900">Haazir</h1>
                <span className="text-xs text-gray-500 -mt-1">
                  {getRoleDisplayName(user.role)} Portal
                </span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            {navigation.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                exact={item.path === `/${user.role}`}
                className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 flex items-center space-x-2 transition-colors duration-200"
                activeClassName="bg-blue-100 text-blue-700"
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          {/* Profile Section */}
          <div className="flex items-center space-x-4">
            {/* Profile Dropdown */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center space-x-2 text-sm bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 p-1"
              >
                <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="hidden sm:block text-gray-700 font-medium">
                  {user.name || user.email.split('@')[0]}
                </span>
              </button>

              {/* Profile Dropdown Menu */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user.name || user.email.split('@')[0]}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <span className="inline-block text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full mt-1">
                        {getRoleDisplayName(user.role)}
                      </span>
                    </div>
                    
                    <NavLink
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      {icons.profile}
                      <span>Profile</span>
                    </NavLink>
                    
                    <button
                      onClick={toggleTheme}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {theme === 'dark' ? icons.sun : icons.moon}
                      <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {icons.logout}
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden relative z-50">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMobileMenu();
                }}
                data-mobile-menu-button="true"
                className="relative inline-flex items-center justify-center p-3 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 active:bg-gray-200 touch-manipulation select-none"
                style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <span className="sr-only">{isMobileMenuOpen ? 'Close menu' : 'Open menu'}</span>
                {/* Animated hamburger icon */}
                <div className="w-6 h-6 flex flex-col justify-center items-center pointer-events-none">
                  <span 
                    className={`block w-5 h-0.5 bg-current transform transition-all duration-300 ease-in-out ${
                      isMobileMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'
                    }`} 
                  />
                  <span 
                    className={`block w-5 h-0.5 bg-current transition-all duration-300 ease-in-out ${
                      isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                    }`} 
                  />
                  <span 
                    className={`block w-5 h-0.5 bg-current transform transition-all duration-300 ease-in-out ${
                      isMobileMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'
                    }`} 
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {(isMobileMenuOpen || isAnimating) && (
        <div 
          className={`fixed inset-0 bg-black z-40 md:hidden transition-opacity duration-300 ${
            isMobileMenuOpen && !isAnimating ? 'opacity-50' : 'opacity-0'
          }`}
          style={{ top: '64px' }}
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Slide-out Panel */}
      {(isMobileMenuOpen || isAnimating) && (
        <div 
          data-mobile-menu
          className={`fixed top-16 right-0 w-full sm:w-80 h-[calc(100vh-64px)] bg-white shadow-2xl z-50 md:hidden transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen && !isAnimating ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* User Info Section */}
            <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold">
                    {user?.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{user?.name || user?.email.split('@')[0]}</p>
                  <p className="text-sm text-blue-100 truncate">{user?.email}</p>
                  <span className="inline-block text-xs px-2 py-0.5 bg-white/20 rounded-full mt-1">
                    {getRoleDisplayName(user?.role || '')}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 overflow-y-auto px-4 py-4">
              <div className="space-y-1">
                {navigation.map((item, index) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    exact={item.path === `/${user?.role}`}
                    className="flex items-center space-x-3 px-4 py-4 rounded-xl text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 touch-manipulation"
                    activeClassName="bg-blue-50 text-blue-700 font-semibold"
                    onClick={toggleMobileMenu}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      {item.icon}
                    </span>
                    <span className="text-base">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </nav>

            {/* Bottom Actions */}
            <div className="border-t border-gray-200 px-4 py-4 space-y-2">
              <NavLink
                to="/profile"
                className="flex items-center space-x-3 px-4 py-4 rounded-xl text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 touch-manipulation"
                onClick={toggleMobileMenu}
              >
                <span className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  {icons.profile}
                </span>
                <span className="text-base">Profile Settings</span>
              </NavLink>
              
              <button
                onClick={() => { toggleTheme(); toggleMobileMenu(); }}
                className="flex items-center space-x-3 w-full px-4 py-4 rounded-xl text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 touch-manipulation"
              >
                <span className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  {theme === 'dark' ? icons.sun : icons.moon}
                </span>
                <span className="text-base">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
              </button>
              
              <button
                onClick={() => { handleLogout(); toggleMobileMenu(); }}
                className="flex items-center space-x-3 w-full px-4 py-4 rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 active:bg-red-100 transition-all duration-200 touch-manipulation"
              >
                <span className="flex-shrink-0 w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                  {icons.logout}
                </span>
                <span className="text-base font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default CleanNavbar;